import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import ChatWidget from "@/components/ChatWidget";
import { SMS_ENABLED } from "@/lib/api";
import {
  SquaresFour, Users, Wrench, FileText, Briefcase, CalendarBlank,
  Receipt, UsersThree, SignOut, Drop, Clock, Timer, UserPlus, CaretDown, Kanban, ChartBar, List, X, Sun, Gear,
} from "@phosphor-icons/react";

// Top single items (above the Customers group)
const TOP = [
  { to: "/", label: "Dashboard", icon: SquaresFour, end: true, roles: ["admin", "crew"] },
  { to: "/my-day", label: "My Day", icon: Sun, roles: ["admin", "crew"] },
  { to: "/intake", label: "Intake", icon: UserPlus, roles: ["admin"] },
  { to: "/pipeline", label: "Pipeline", icon: Kanban, roles: ["admin"] },
  { to: "/reports", label: "Reports", icon: ChartBar, roles: ["admin"] },
  { to: "/schedule", label: "Schedule", icon: CalendarBlank, roles: ["admin", "crew"] },
];

// Collapsible category
const GROUPS = [
  {
    label: "Customers", icon: Users, roles: ["admin", "crew"],
    items: [
      { to: "/clients", label: "Clients", icon: Users, roles: ["admin"] },
      { to: "/quotes", label: "Quotes", icon: FileText, roles: ["admin"] },
      { to: "/jobs", label: "Jobs", icon: Briefcase, roles: ["admin", "crew"] },
      { to: "/invoices", label: "Invoices", icon: Receipt, roles: ["admin"] },
    ],
  },
];

// Bottom time-tracking pair (side by side)
const TIME = [
  { to: "/time", label: "Time Clock", icon: Clock, roles: ["admin", "crew"] },
  { to: "/time-hub", label: "Time Hub", icon: Timer, roles: ["admin"] },
];

// Bottom single items (below the time pair)
const BOTTOM = [
  { to: "/services", label: "Services", icon: Wrench, roles: ["admin"] },
  { to: "/team", label: "Team", icon: UsersThree, roles: ["admin"] },
  { to: "/settings", label: "Settings", icon: Gear, roles: ["admin"] },
];

const itemClass = ({ isActive }) =>
  `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors duration-200 ${
    isActive ? "bg-[#00B4D8] text-white" : "text-slate-300 hover:bg-white/5 hover:text-white"
  }`;

function NavItem({ n }) {
  const Icon = n.icon;
  return (
    <NavLink to={n.to} end={n.end} data-testid={`nav-${n.label.toLowerCase()}`} className={itemClass}>
      <Icon size={20} weight="duotone" />
      {n.label}
    </NavLink>
  );
}

function NavGroup({ group, role }) {
  const [open, setOpen] = useState(true);
  const items = group.items.filter((i) => i.roles.includes(role));
  if (!items.length) return null;
  const Icon = group.icon;
  return (
    <div>
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid={`nav-group-${group.label.toLowerCase()}`}
        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-[11px] font-bold uppercase tracking-[0.15em] text-slate-400 hover:text-white transition-colors duration-200"
      >
        <Icon size={18} weight="duotone" />
        <span className="flex-1 text-left">{group.label}</span>
        <CaretDown size={14} weight="bold" className={`transition-transform duration-200 ${open ? "" : "-rotate-90"}`} />
      </button>
      {open && (
        <div className="mt-1 ml-3 pl-3 border-l border-white/10 space-y-1">
          {items.map((i) => <NavItem key={i.to} n={i} />)}
        </div>
      )}
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const role = user?.role;
  const [mobileOpen, setMobileOpen] = useState(false);

  const top = TOP.filter((n) => n.roles.includes(role));
  const timeItems = TIME.filter((n) => n.roles.includes(role));
  const bottom = BOTTOM.filter((n) => n.roles.includes(role));

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  // Close the mobile drawer whenever a navigation link is tapped
  const handleNavClick = (e) => {
    if (e.target.closest("a")) setMobileOpen(false);
  };

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-[#0B1726] text-white flex items-center justify-between px-4 z-40">
        <button onClick={() => setMobileOpen(true)} data-testid="mobile-menu-button" className="p-1.5 -ml-1.5 rounded-md hover:bg-white/10">
          <List size={24} weight="bold" />
        </button>
        <img src="/adpw-logo.png" alt="All Day Power Washing" className="h-9 w-auto" />
        <div className="w-8" />
      </div>

      {/* Backdrop (mobile only) */}
      {mobileOpen && (
        <div onClick={() => setMobileOpen(false)} data-testid="mobile-backdrop" className="lg:hidden fixed inset-0 bg-black/50 z-40" />
      )}

      {/* Sidebar */}
      <aside
        className={`w-64 max-w-[85vw] bg-[#0B1726] text-white flex flex-col fixed h-screen z-50 transition-transform duration-300 ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        } lg:translate-x-0`}
      >
        <div className="px-5 py-5 border-b border-white/10 flex justify-center relative">
          <img src="/adpw-logo.png" alt="All Day Power Washing" className="w-40 h-auto drop-shadow-lg" data-testid="sidebar-logo" />
          <button onClick={() => setMobileOpen(false)} className="lg:hidden absolute right-4 top-4 p-1.5 rounded-md hover:bg-white/10" data-testid="mobile-close-button">
            <X size={22} weight="bold" />
          </button>
        </div>

        <nav onClick={handleNavClick} className="flex-1 px-3 py-5 flex flex-col gap-1 overflow-y-auto">
          {top.map((n) => <NavItem key={n.to} n={n} />)}
          {GROUPS.map((g) => <NavGroup key={g.label} group={g} role={role} />)}

          {/* pushes the cluster below to the bottom */}
          <div className="flex-1 min-h-[16px]" />

          {timeItems.length > 0 && (
            <div className={`grid gap-2 ${timeItems.length > 1 ? "grid-cols-2" : "grid-cols-1"}`}>
              {timeItems.map((n) => {
                const Icon = n.icon;
                return (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    data-testid={`nav-${n.label.toLowerCase()}`}
                    className={({ isActive }) =>
                      `flex flex-col items-center justify-center gap-1 px-2 py-3 rounded-md text-xs font-semibold transition-colors duration-200 ${
                        isActive ? "bg-[#00B4D8] text-white" : "bg-white/5 text-slate-300 hover:bg-white/10 hover:text-white"
                      }`
                    }
                  >
                    <Icon size={20} weight="duotone" />
                    {n.label}
                  </NavLink>
                );
              })}
            </div>
          )}
          {bottom.map((n) => <NavItem key={n.to} n={n} />)}
        </nav>

        <div className="px-3 py-4 border-t border-white/10">
          <div className="flex items-center gap-3 px-3 py-2">
            <div className="w-9 h-9 rounded-full bg-slate-700 flex items-center justify-center text-sm font-bold shrink-0 overflow-hidden">
              {user?.avatar_url ? (
                <img src={user.avatar_url} alt="" className="w-full h-full object-cover" />
              ) : (
                user?.name?.[0]?.toUpperCase() || "U"
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate">{user?.name}</div>
              <div className="text-[11px] text-slate-400 capitalize">{user?.role}</div>
            </div>
          </div>
          <button
            onClick={doLogout}
            data-testid="logout-button"
            className="mt-2 w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-slate-300 hover:bg-white/5 hover:text-white transition-colors duration-200"
          >
            <SignOut size={18} weight="bold" /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 lg:ml-64 min-h-screen pt-14 lg:pt-0">{children}</main>

      {role === "admin" && SMS_ENABLED && <ChatWidget />}
    </div>
  );
}

export function PageHeader({ title, subtitle, actions }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-3 mb-6 sm:mb-8">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">{title}</h1>
        {subtitle && <p className="text-slate-500 mt-1 text-sm">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}
